import type {PortalType} from './constants/auth';

/**
 * The profile facts a portal decision needs, reduced to booleans.
 *
 * This is the intersection of what the enriched session carries on the frontend
 * and what the API can select at sign-in, before a session exists. Keeping the
 * decision on this shape rather than on `User` is what lets the frontend guard
 * and the server-side sign-in check share one implementation.
 */
export interface PortalProfileShape {
  hasInternalProfile: boolean;
  hasClientProfile: boolean;
  hasBrokerProfile: boolean;
  hasTpaProfile: boolean;
  hasGroupProfile: boolean;
}

/** Whether a user holds any profile that grants access to some portal. */
export function hasAnyPortalProfile(profiles: PortalProfileShape): boolean {
  return (
    profiles.hasInternalProfile ||
    profiles.hasClientProfile ||
    profiles.hasBrokerProfile ||
    profiles.hasTpaProfile ||
    profiles.hasGroupProfile
  );
}

/**
 * Whether these profiles may operate in a portal of `portalType`.
 *
 * This is the **type** axis only: it separates the CBS internal portal from the
 * client portals. It does *not* separate one tenant's portal from another's.
 * TODO: Add a stable client key to create a tenant axis, enabling client-specific
 * portal access checks: https://app.asana.com/1/1206214889537900/project/1206881612175705/task/1218418427138239?focus=true
 */
export function profilesMatchPortalType(
  profiles: PortalProfileShape,
  portalType: PortalType,
): boolean {
  if (portalType === 'internal') {
    return profiles.hasInternalProfile;
  }

  return (
    profiles.hasClientProfile ||
    profiles.hasBrokerProfile ||
    profiles.hasTpaProfile ||
    profiles.hasGroupProfile
  );
}
